import {
  forwardRef,
  HTMLAttributes,
  Ref,
  RefAttributes,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type JSX,
} from "react";

import MuiAutocomplete, {
  AutocompleteOwnerState,
  AutocompleteRenderGroupParams,
  AutocompleteRenderInputParams,
  AutocompleteRenderOptionState,
  AutocompleteValue,
} from "@mui/material/Autocomplete";
import Stack from "@mui/material/Stack";
import { Theme } from "@mui/material/styles";
import { FieldValues, useController } from "react-hook-form";

import { AutocompleteOption } from "./AutocompleteOption/AutocompleteOption";
import { AutocompleteV2Paper, AutocompleteV2PaperProvider } from "./AutocompleteV2PaperContext";
import {
  AutocompleteV2Option,
  BaseDashboardAutocompleteV2Props,
  AutocompleteFooterConfig,
  FormDashboardAutocompleteV2Props,
  DashboardAutocompleteV2Props,
} from "./types";

import { mergeSx } from "@/common/helpers/mergeSx";
import { getLabel, isInputRequired } from "@/common/helpers/validators";
import { useCurrentInputVibe } from "@/common/hooks/useCurrentInputVibe";
import { useFormInputRefs } from "@/common/hooks/useFormInputRefs";
import { useTranslate } from "@/common/hooks/useTranslate";
import { useValidators } from "@/common/hooks/useValidators";
import { DashboardChip } from "@/dashboard-design-system/DashboardChip/DashboardChip";
import { DashboardDivider } from "@/dashboard-design-system/Divider";
import { DashboardIcon } from "@/dashboard-design-system/Icon";
import { DashboardTextField } from "@/dashboard-design-system/TextField/DashboardTextField";
import { DashboardTypography } from "@/dashboard-design-system/Typography";
import { ChevronDown, ChevronUp, Cross as Close } from "@/icons/outlined";

const MAX_CHIP_WIDTH = 150;
const OVERFLOW_CHIP_WIDTH = 100;
const MARGIN_BUFFER = 16;
const INPUT_MIN_WIDTH = 60;

function DashboardAutocompleteV2Inner<
  Value,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false,
>(
  {
    vibe,
    value,
    options,
    onChange,
    onInputChange,
    inputValue: controlledInputValue,
    label,
    error,
    errorMessage,
    tooltipText,
    disabled,
    placeholder = "Choose...",
    searchPlaceholder = "Search...",
    multiple,
    autoFocus,
    sx,
    size,
    optionSize = "standard",
    open: controlledOpen,
    onOpen,
    onClose,
    selectAll = false,
    selectAllLabel = "Select all",
    allSelectedLabel,
    footer,
    renderOption: externalRenderOption,
    renderTags: externalRenderTags,
    renderGroup,
    PaperComponent,
    hideDropdownIcon,
    hideClearIcon,
    popupIcon,
    chipProps = { shape: "rounded", variant: "filled" },
    overflowLabel,
    disableTagOverflow = false,
    inputRef: externalInputRef,
    openOnFocus,
    noAsterisk,
    endAdornment,
    freeSolo,
    disableClearable,
    disableCloseOnSelect,
    limitTags = 3,
    loading,
    loadingText,
    noOptionsText,
    filterOptions,
    getOptionLabel,
    getOptionDisabled,
    isOptionEqualToValue: externalIsOptionEqualToValue,
    groupBy,
    defaultValue,
    ListboxProps,
    onFocus,
    onBlur,
    slotProps,
    name,
    "data-test": dataTest,
    ...rest
  }: BaseDashboardAutocompleteV2Props<Value, Multiple, DisableClearable, FreeSolo>,
  ref: Ref<HTMLInputElement>
) {
  const currentInputVibe = useCurrentInputVibe(vibe);
  const { t } = useTranslate();
  const [internalIsOpen, setInternalIsOpen] = useState(Boolean(controlledOpen));
  const [internalInputValue, setInternalInputValue] = useState(controlledInputValue || "");
  const [internalValue, setInternalValue] = useState(value);
  const internalInputRef = useRef<HTMLInputElement>(null);
  const refCallback = useFormInputRefs({ inputRefs: [internalInputRef, externalInputRef] });

  // Chip overflow measurement
  const containerRef = useRef<HTMLDivElement>(null);
  const [visibleTagCount, setVisibleTagCount] = useState(limitTags);

  const allSelected = useMemo(() => {
    if (!multiple || !Array.isArray(internalValue)) {
      return false;
    }
    return options.length > 0 && (internalValue as Value[]).length === options.length;
  }, [multiple, internalValue, options.length]);

  // Sync controlled props
  useEffect(() => {
    if (Boolean(controlledOpen) !== internalIsOpen) {
      setInternalIsOpen(Boolean(controlledOpen));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controlledOpen]);

  useEffect(() => {
    setInternalValue(value);
  }, [value]);

  useEffect(() => {
    setInternalInputValue(controlledInputValue || "");
  }, [controlledInputValue]);

  // Chip overflow calculation
  useEffect(() => {
    const measurementTarget = containerRef.current?.parentElement;
    if (!measurementTarget) {
      return;
    }

    const resizeObserver = new ResizeObserver(() => {
      const width = measurementTarget.offsetWidth;
      if (width <= 0) {
        return;
      }

      const tags = multiple && Array.isArray(internalValue) ? (internalValue as Value[]) : [];
      if (tags.length === 0) {
        setVisibleTagCount(0);
        return;
      }

      const spaceForTags = width - INPUT_MIN_WIDTH - MARGIN_BUFFER;
      if (spaceForTags <= 0) {
        setVisibleTagCount(0);
        return;
      }

      const totalWidth = tags.length * MAX_CHIP_WIDTH;
      if (totalWidth <= spaceForTags) {
        setVisibleTagCount(tags.length);
        return;
      }

      const availableForTags = spaceForTags - OVERFLOW_CHIP_WIDTH;
      setVisibleTagCount(Math.max(0, Math.floor(availableForTags / MAX_CHIP_WIDTH)));
    });

    resizeObserver.observe(measurementTarget);
    return () => resizeObserver.disconnect();
  }, [multiple, internalValue]);

  const handleOnOpen = useCallback(() => {
    if (controlledOpen === undefined) {
      setInternalIsOpen(true);
    }
    onOpen?.();
  }, [onOpen, controlledOpen]);

  const closeAutocomplete = useCallback(() => {
    if (multiple && !freeSolo) {
      setInternalInputValue("");
    }
    if (controlledOpen === undefined) {
      setInternalIsOpen(false);
    }
    onClose?.();
    setTimeout(() => internalInputRef.current?.blur(), 0);
  }, [freeSolo, multiple, onClose, controlledOpen]);

  const handleOnClose = useCallback(() => {
    closeAutocomplete();
  }, [closeAutocomplete]);

  const handleOnApply = useCallback(() => {
    closeAutocomplete();
  }, [closeAutocomplete]);

  const handleOnClear = useCallback(() => {
    const newValue = multiple
      ? ([] as AutocompleteValue<Value, Multiple, DisableClearable, FreeSolo>)
      : (null as AutocompleteValue<Value, Multiple, DisableClearable, FreeSolo>);
    onChange?.(newValue);
    setInternalValue(newValue);
    closeAutocomplete();
  }, [closeAutocomplete, multiple, onChange]);

  const handleSelectAll = useCallback(() => {
    if (!multiple) {
      return;
    }
    const newValue = allSelected
      ? ([] as unknown as AutocompleteValue<Value, Multiple, DisableClearable, FreeSolo>)
      : (options as unknown as AutocompleteValue<Value, Multiple, DisableClearable, FreeSolo>);
    setInternalValue(newValue);
    onChange?.(newValue);
  }, [multiple, allSelected, options, onChange]);

  const isOptionEqualToValue = useCallback(
    (option: Value, val: Value) => {
      if (externalIsOptionEqualToValue) {
        return externalIsOptionEqualToValue(option, val);
      }
      if (typeof option === "string" || typeof val === "string") {
        return option === val;
      }
      return (option as AutocompleteV2Option).value === (val as AutocompleteV2Option)?.value;
    },
    [externalIsOptionEqualToValue]
  );

  const renderDefaultOption = useCallback(
    (props: HTMLAttributes<HTMLLIElement>, option: Value, { selected }: { selected: boolean }) => {
      const opt = option as AutocompleteV2Option;
      const optLabel = typeof option === "string" ? option : opt.label;
      const key = typeof option === "string" ? option : String(opt.value);

      return (
        <AutocompleteOption
          {...props}
          key={key}
          label={optLabel}
          secondaryLabel={typeof option !== "string" ? opt.secondaryLabel : undefined}
          icon={typeof option !== "string" ? opt.icon : undefined}
          checkbox={Boolean(multiple)}
          checked={selected}
          selected={selected}
          disabled={getOptionDisabled?.(option) ?? false}
          size={optionSize}
          {...slotProps?.option}
        />
      );
    },
    [multiple, optionSize, getOptionDisabled, slotProps?.option]
  );

  const renderDefaultGroup = useCallback(
    (params: AutocompleteRenderGroupParams) => (
      <li key={params.key}>
        <DashboardDivider
          sx={(theme) => ({
            position: "sticky",
            top: "-8px",
            zIndex: 1,
            backgroundColor: theme.palette.surface.neutral.white,
            padding: theme.spacing(1, 2),
          })}
        >
          <DashboardTypography variant="caption" sx={(theme) => ({ color: theme.palette.text.neutral.quiet })}>
            {params.group}
          </DashboardTypography>
        </DashboardDivider>
        <ul style={{ padding: 0, margin: 0, listStyle: "none" }}>{params.children}</ul>
      </li>
    ),
    []
  );

  const chipSx = (theme: Theme) => ({
    flexShrink: 1,
    minWidth: 0,
    ...theme.mixins.truncateOverflow,
  });

  const renderTags = useCallback(
    (tagList: Value[]) => {
      if (allSelected && allSelectedLabel) {
        return (
          <Stack ref={containerRef} direction="row" gap={0.5} flexWrap="nowrap" overflow="hidden">
            <DashboardChip
              variant={chipProps.variant}
              vibe="brand"
              shape={chipProps.shape}
              label={allSelectedLabel}
              onDelete={handleOnClear}
              sx={chipSx}
            />
          </Stack>
        );
      }

      const shouldShowOverflow = !disableTagOverflow && visibleTagCount < tagList.length;
      const overflowCount = tagList.length - visibleTagCount;
      const visibleTags = shouldShowOverflow ? tagList.slice(0, visibleTagCount) : tagList;

      return (
        <Stack ref={containerRef} direction="row" gap={0.5} flexWrap="nowrap" overflow="hidden">
          {visibleTags.map((tag) => {
            const opt = tag as AutocompleteV2Option;
            const tagLabel = typeof tag === "string" ? tag : opt.label;
            const tagKey = typeof tag === "string" ? tag : String(opt.value);
            const tagVibe = typeof tag === "string" ? "neutral" : (opt.vibe ?? "brand");

            return (
              <DashboardChip
                key={tagKey}
                variant={chipProps.variant}
                vibe={tagVibe}
                shape={chipProps.shape}
                label={tagLabel}
                onDelete={() => {
                  if (!multiple || !Array.isArray(internalValue)) {
                    return;
                  }
                  const newValue = (internalValue as Value[]).filter((v) => !isOptionEqualToValue(v, tag));
                  setInternalValue(newValue as AutocompleteValue<Value, Multiple, DisableClearable, FreeSolo>);
                  onChange?.(newValue as AutocompleteValue<Value, Multiple, DisableClearable, FreeSolo>);
                }}
                sx={chipSx}
              />
            );
          })}
          {shouldShowOverflow && (
            <DashboardChip
              variant={chipProps.variant}
              vibe="brand"
              shape={chipProps.shape}
              label={overflowLabel ? overflowLabel(overflowCount) : `${overflowCount} others...`}
              onDelete={() => {
                if (!multiple || !Array.isArray(internalValue)) {
                  return;
                }
                const newValue = (internalValue as Value[]).slice(0, visibleTagCount) as AutocompleteValue<
                  Value,
                  Multiple,
                  DisableClearable,
                  FreeSolo
                >;
                setInternalValue(newValue);
                onChange?.(newValue);
              }}
            />
          )}
        </Stack>
      );
    },
    [
      allSelected,
      allSelectedLabel,
      visibleTagCount,
      chipProps,
      multiple,
      internalValue,
      isOptionEqualToValue,
      onChange,
      handleOnClear,
      overflowLabel,
      disableTagOverflow,
    ]
  );

  const hasSelectedItems = multiple && Array.isArray(internalValue) && (internalValue as Value[]).length > 0;
  const activePlaceholder = hasSelectedItems ? undefined : internalIsOpen ? searchPlaceholder : placeholder;

  const renderInput = useCallback(
    ({
      InputProps: { endAdornment: inputEndAdornment, ref: inputContainerRef, startAdornment },
      ...inputRest
    }: AutocompleteRenderInputParams) => (
      <DashboardTextField
        {...inputRest}
        vibe={currentInputVibe}
        data-test={dataTest}
        size={size}
        noAsterisk={noAsterisk}
        endAdornment={
          <>
            {inputEndAdornment}
            {endAdornment}
          </>
        }
        error={error}
        errorMessage={errorMessage}
        label={label}
        tooltipText={tooltipText}
        ref={inputContainerRef}
        startAdornment={startAdornment}
        inputRef={refCallback}
        placeholder={activePlaceholder}
        autoFocus={autoFocus}
        name={name}
        sx={() => ({
          "& .MuiInputBase-root": {
            overflow: "hidden",
            display: "flex",
            flexWrap: "nowrap",
            transition: "background-color 0.2s ease-in, border-color 0.2s ease-in, box-shadow 0.2s ease-in",
          },
          "& .MuiInputBase-input": {
            flexShrink: 0,
            width: 10,
            ...(hasSelectedItems && { caretColor: "transparent" }),
          },
        })}
      />
    ),
    [
      currentInputVibe,
      dataTest,
      size,
      noAsterisk,
      endAdornment,
      error,
      errorMessage,
      label,
      tooltipText,
      refCallback,
      activePlaceholder,
      autoFocus,
      name,
      hasSelectedItems,
    ]
  );

  const footerConfig = useMemo<AutocompleteFooterConfig | undefined>(() => {
    if (footer) {
      return footer;
    }
    if (multiple) {
      return { type: "default" };
    }
    return undefined;
  }, [footer, multiple]);

  const ResolvedPaper = PaperComponent ?? AutocompleteV2Paper;

  return (
    <AutocompleteV2PaperProvider
      selectAll={selectAll}
      multiple={Boolean(multiple)}
      allSelected={allSelected}
      selectAllLabel={selectAllLabel}
      optionSize={optionSize}
      onSelectAll={handleSelectAll}
      onApply={handleOnApply}
      onClear={handleOnClear}
      footerConfig={footerConfig}
      paperSx={slotProps?.paper?.sx}
      listboxSx={slotProps?.listbox?.sx}
    >
      <MuiAutocomplete<Value, Multiple, DisableClearable, FreeSolo>
        {...rest}
        options={options}
        multiple={multiple}
        freeSolo={freeSolo}
        loading={loading}
        loadingText={loadingText ?? t("common", "loading")}
        noOptionsText={noOptionsText ?? t("common", "noOptions")}
        disableClearable={disableClearable}
        disableCloseOnSelect={disableCloseOnSelect ?? Boolean(multiple)}
        filterOptions={filterOptions}
        getOptionLabel={getOptionLabel}
        getOptionDisabled={getOptionDisabled}
        isOptionEqualToValue={isOptionEqualToValue}
        groupBy={groupBy}
        defaultValue={defaultValue}
        limitTags={limitTags}
        ListboxProps={{
          ...ListboxProps,
          style: { maxHeight: "none", overflow: "visible", padding: 0, ...ListboxProps?.style },
        }}
        onFocus={(event) => {
          if (openOnFocus) {
            handleOnOpen();
          }
          onFocus?.(event);
        }}
        onBlur={onBlur}
        disabled={disabled}
        clearIcon={
          hideClearIcon || !hasSelectedItems ? null : (
            <DashboardIcon
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleOnClear();
              }}
            >
              <Close />
            </DashboardIcon>
          )
        }
        popupIcon={
          hideDropdownIcon ? null : (
            <DashboardIcon size="small">
              {popupIcon ?? (internalIsOpen ? <ChevronUp /> : <ChevronDown />)}
            </DashboardIcon>
          )
        }
        renderInput={renderInput}
        ref={ref}
        renderOption={
          (externalRenderOption || renderDefaultOption) as (
            props: HTMLAttributes<HTMLLIElement>,
            option: Value,
            state: AutocompleteRenderOptionState,
            ownerState: AutocompleteOwnerState<Value, Multiple, DisableClearable, FreeSolo>
          ) => React.ReactNode
        }
        renderTags={externalRenderTags || (renderTags as (value: Value[]) => React.ReactNode)}
        renderGroup={renderGroup || renderDefaultGroup}
        inputValue={multiple ? internalInputValue : undefined}
        onInputChange={(event, newInputValue, reason) => {
          if (multiple) {
            setInternalInputValue(newInputValue);
          }
          onInputChange?.(event as React.ChangeEvent<unknown>, newInputValue, reason);
        }}
        open={internalIsOpen}
        onOpen={handleOnOpen}
        onClose={handleOnClose}
        onChange={(_evt, newValue) => {
          setInternalValue(newValue);
          onChange?.(newValue);
        }}
        value={internalValue}
        PaperComponent={ResolvedPaper}
        data-test="dashboard-autocomplete-v2"
        sx={mergeSx(
          (theme: Theme) => ({
            "& .MuiAutocomplete-endAdornment": {
              position: "static",
              display: "flex",
              padding: 0,
              transform: "none",
            },
            "& .MuiAutocomplete-clearIndicator": {
              borderRadius: theme.radius(0.75),
              margin: 0,
              transition: "opacity 0.2s ease-in",
            },
            "& .MuiAutocomplete-popupIndicator": {
              borderRadius: theme.radius(0.75),
              padding: theme.spacing(0.5),
              flexShrink: 0,
              ...(popupIcon ? { transform: "none" } : {}),
            },
            "& .MuiAutocomplete-option": {
              padding: "0 !important",
            },
            "& .MuiAutocomplete-paper": {
              boxShadow: theme.boxShadow.base.mid,
            },
            "& .MuiAutocomplete-input": {
              flexShrink: 0,
              width: 10,
            },
            "& .MuiAutocomplete-inputRoot": {
              overflow: "hidden",
              display: "flex",
              flexWrap: "nowrap",
            },
            "& .MuiAutocomplete-listbox": {
              padding: 0,
            },
          }),
          sx
        )}
      />
    </AutocompleteV2PaperProvider>
  );
}

DashboardAutocompleteV2Inner.displayName = "DashboardAutocompleteV2";

const BaseDashboardAutocompleteV2 = forwardRef(DashboardAutocompleteV2Inner) as <
  Value = string | AutocompleteV2Option,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false,
>(
  props: BaseDashboardAutocompleteV2Props<Value, Multiple, DisableClearable, FreeSolo> & RefAttributes<HTMLInputElement>
) => JSX.Element;

// ---------------------------------------------------------------------------
// Form wrapper (react-hook-form integration)
// ---------------------------------------------------------------------------

function FormDashboardAutocompleteV2Inner<
  T extends FieldValues,
  Value = string | AutocompleteV2Option,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false,
>(
  {
    control,
    defaultValue,
    name,
    onChange,
    rules,
    noAsterisk,
    ...rest
  }: FormDashboardAutocompleteV2Props<T, Value, Multiple, DisableClearable, FreeSolo>,
  ref: Ref<HTMLInputElement>
) {
  const validators = useValidators(rules);
  const isRequired = isInputRequired(validators);
  const { field } = useController<T>({
    name,
    control,
    defaultValue: defaultValue as never,
    rules: validators,
  });

  const handleChange = useCallback(
    (value: AutocompleteValue<Value, Multiple, DisableClearable, FreeSolo> | undefined) => {
      onChange?.(value);
      field.onChange(value);
    },
    [field, onChange]
  );

  return (
    <BaseDashboardAutocompleteV2
      {...rest}
      inputRef={field.ref}
      onChange={handleChange}
      value={field.value as AutocompleteValue<Value, Multiple, DisableClearable, FreeSolo>}
      ref={ref}
      label={getLabel(isRequired, rest.label, noAsterisk)}
      name={name}
    />
  );
}

FormDashboardAutocompleteV2Inner.displayName = "FormDashboardAutocompleteV2";

const FormDashboardAutocompleteV2 = forwardRef(FormDashboardAutocompleteV2Inner) as unknown as <
  T extends FieldValues,
  Value = string | AutocompleteV2Option,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false,
>(
  props: FormDashboardAutocompleteV2Props<T, Value, Multiple, DisableClearable, FreeSolo> &
    RefAttributes<HTMLInputElement>
) => JSX.Element;

// ---------------------------------------------------------------------------
// Entry point
// ---------------------------------------------------------------------------

function DashboardAutocompleteV2Entry<
  Value,
  T extends FieldValues,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined,
>(
  { control, rules, name, ...rest }: DashboardAutocompleteV2Props<Value, Multiple, DisableClearable, FreeSolo, T>,
  ref: Ref<HTMLInputElement>
) {
  return control && name ? (
    <FormDashboardAutocompleteV2<T, Value, Multiple, DisableClearable, FreeSolo>
      {...rest}
      ref={ref}
      control={control}
      rules={rules}
      name={name}
    />
  ) : (
    <BaseDashboardAutocompleteV2<Value, Multiple, DisableClearable, FreeSolo> {...rest} name={name} ref={ref} />
  );
}

DashboardAutocompleteV2Entry.displayName = "DashboardAutocompleteV2";

export const DashboardAutocompleteV2 = forwardRef(DashboardAutocompleteV2Entry) as <
  Value = string | AutocompleteV2Option,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false,
  T extends FieldValues = FieldValues,
>(
  props: DashboardAutocompleteV2Props<Value, Multiple, DisableClearable, FreeSolo, T> & RefAttributes<HTMLInputElement>
) => JSX.Element;
